import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Layers, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function EditProposalPage() {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [schemaTree, setSchemaTree] = useState(null);
  const [selectedAttrs, setSelectedAttrs] = useState(new Set());
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/proposals/${proposalId}`);
        setProposal(r.data);
        setTitle(r.data.title);
        setDescription(r.data.description || '');
        setSelectedAttrs(new Set(r.data.attribute_ids || []));
        const tree = await api.get(`/schemas/${r.data.schema_id}/full`);
        setSchemaTree(tree.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [proposalId]);

  const toggleAttr = (id) => {
    setSelectedAttrs(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleEntity = (entity) => {
    const ids = [];
    (entity.groups || []).forEach(g => g.attributes.forEach(a => ids.push(a.attribute_id)));
    (entity.ungrouped_attributes || []).forEach(a => ids.push(a.attribute_id));
    const allSel = ids.every(id => selectedAttrs.has(id));
    setSelectedAttrs(prev => { const n = new Set(prev); ids.forEach(id => allSel ? n.delete(id) : n.add(id)); return n; });
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      await api.put(`/proposals/${proposalId}`, {
        title: title.trim(), description: description.trim(),
        attribute_ids: [...selectedAttrs],
      });
      toast.success('Proposal updated');
      navigate('/proposals');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div></div>);
  }
  if (!proposal || !schemaTree) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="text-center py-12 text-[#9CA3AF]">Proposal not found</div></div>);
  }

  return (
    <div className="min-h-screen bg-white" data-testid="edit-proposal-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/proposals')} className="h-7 px-2 rounded-none text-[#4B5563]" data-testid="back-to-proposals">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Proposals
          </Button>
          <span className="text-[#9CA3AF]">/</span>
          <span className="text-sm font-medium text-[#0A0A0A]">Edit</span>
        </div>

        <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A] mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>Edit Proposal</h1>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Title</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} className="rounded-none mt-1" data-testid="edit-proposal-title" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Schema</Label>
              <Input value={schemaTree.name} disabled className="rounded-none mt-1 bg-zinc-50" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#4B5563]">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} className="rounded-none mt-1" rows={2} data-testid="edit-proposal-desc" />
          </div>

          <div>
            <Label className="text-xs font-semibold text-[#4B5563]">Attributes ({selectedAttrs.size} selected)</Label>
            <div className="border border-[#E5E7EB] mt-2" data-testid="edit-attr-selector">
              {(schemaTree.entities || []).map(entity => {
                const entityIds = [];
                (entity.groups || []).forEach(g => g.attributes.forEach(a => entityIds.push(a.attribute_id)));
                (entity.ungrouped_attributes || []).forEach(a => entityIds.push(a.attribute_id));
                const allSel = entityIds.every(id => selectedAttrs.has(id));
                return (
                  <div key={entity.entity_id}>
                    <div className="flex items-center gap-3 px-4 py-2 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <Checkbox checked={allSel} onCheckedChange={() => toggleEntity(entity)} className="h-4 w-4 rounded-none" />
                      <Layers className="w-4 h-4 text-[#002FA7]" strokeWidth={1.5} />
                      <span className="text-sm font-semibold text-[#0A0A0A] flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{entity.name}</span>
                      <span className="text-[10px] text-[#9CA3AF]">{entityIds.filter(id => selectedAttrs.has(id)).length}/{entityIds.length}</span>
                    </div>
                    {[...(entity.groups || []).flatMap(g => g.attributes), ...(entity.ungrouped_attributes || [])].map(a => (
                      <div key={a.attribute_id} className={`flex items-center gap-3 px-4 py-1.5 pl-12 border-b border-[#E5E7EB] ${selectedAttrs.has(a.attribute_id) ? 'bg-white' : 'bg-zinc-50/50'}`}>
                        <Checkbox checked={selectedAttrs.has(a.attribute_id)} onCheckedChange={() => toggleAttr(a.attribute_id)} className="h-3.5 w-3.5 rounded-none" />
                        <span className={`text-sm flex-1 ${selectedAttrs.has(a.attribute_id) ? 'text-[#0A0A0A]' : 'text-[#9CA3AF]'}`}>{a.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{a.data_type}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving || !title.trim() || selectedAttrs.size === 0} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585] h-10" data-testid="save-proposal-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
